import axios from 'axios';
import type {
  EnhancedAnalysis,
  MarketScanner,
  SimulatorSignal,
  PerformanceMetrics,
  User,
  SentimentExtremesResponse,
  TurnaroundAnalysis,
  OpportunityTrapAnalysis,
  PatternPerformanceResponse,
  MarketResearchOverview,
  MA200Analysis,
  EarningsTranscriptsResponse
} from '../types';

// Use empty string for local dev (Vite proxy handles /api routes)
// Set VITE_API_URL for production deployments
const API_BASE = import.meta.env.VITE_API_URL || '';

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  // Don't overwrite if Authorization header is already set (e.g., OAuth callback)
  if (config.headers.Authorization) {
    return config;
  }

  const token = localStorage.getItem('sentimentedge-auth');
  if (token) {
    try {
      const parsed = JSON.parse(token);
      if (parsed?.state?.token) {
        config.headers.Authorization = `Bearer ${parsed.state.token}`;
      }
    } catch {
      // Invalid token format
    }
  }
  return config;
});

// API Functions

// Analysis - FAST endpoint (reads from pre-computed cache)
export async function getStockFast(ticker: string): Promise<EnhancedAnalysis> {
  const response = await api.get(`/api/stock/${ticker}`);
  return response.data;
}

// Analysis - Enhanced (PRO only, slower - does real-time analysis)
export async function analyzeStock(ticker: string): Promise<EnhancedAnalysis> {
  const response = await api.get(`/api/analyze/${ticker}/enhanced`);
  return response.data;
}

// Analysis with progress callback and longer timeout for generation
export async function analyzeStockWithProgress(
  ticker: string,
  onProgress?: (msg: string) => void
): Promise<EnhancedAnalysis> {
  onProgress?.('Collecting market data...');

  const response = await api.get(`/api/analyze/${ticker}`, {
    timeout: 60000  // 60 second timeout for analysis generation
  });

  return response.data;
}

// Check if analysis data is complete (has actual scores, not zeros/nulls)
export function isDataComplete(data: EnhancedAnalysis): boolean {
  // Check that we have meaningful sentiment data (not zero or null)
  const hasSentiment = data.sentiment?.sentiment_score != null && data.sentiment.sentiment_score !== 0;

  // Check that we have meaningful disconnect data (not zero or null)
  const hasDisconnect = data.disconnect?.disconnect_score != null && data.disconnect.disconnect_score !== 0;

  // Check that we have a valid price
  const hasPrice = (data.technical?.current_price ?? 0) > 0;

  return hasSentiment && hasDisconnect && hasPrice;
}

// Error type for daily limit exceeded
export interface DailyLimitError {
  error: 'daily_analysis_limit_exceeded';
  message: string;
  limit: number;
  used: number;
  upgrade_url: string;
}

export async function lookupStock(ticker: string): Promise<EnhancedAnalysis> {
  const response = await api.get(`/api/lookup/${ticker}`);
  return response.data;
}

export async function getQuickSentiment(ticker: string): Promise<{
  ticker: string;
  sentiment_score: number;
  sentiment_label: string;
  summary: string;
}> {
  const response = await api.get(`/api/sentiment/${ticker}`);
  return response.data;
}

// Scanner - fetches pre-computed scan results
export async function runMarketScanner(
  tickers?: string[],
  sectors?: string[],
  limit?: number
): Promise<MarketScanner> {
  const params: Record<string, string> = {};
  if (tickers?.length) params.tickers = tickers.join(',');
  if (sectors?.length) params.sectors = sectors.join(',');
  if (limit) params.limit = String(limit);

  // Fetch pre-computed scan results (data is updated daily at 6 AM EST)
  const response = await api.get('/api/scan', { params });
  const data = response.data;

  return {
    buy_opportunities: data.buy_opportunities || [],
    sell_signals: data.sell_signals || [],
    scan_time: data.scan_time || new Date().toISOString(),
    stocks_analyzed: data.stocks_analyzed || 0,
  };
}

export async function getTopOpportunities(limit = 10): Promise<MarketScanner> {
  const response = await api.get('/api/stocks/opportunities', { params: { limit } });
  return {
    buy_opportunities: response.data.opportunities || [],
    sell_signals: [],
    scan_time: new Date().toISOString(),
    stocks_analyzed: response.data.opportunities?.length || 0,
  };
}

// Simulator
export async function getSimulatorSignals(
  startDate?: string,
  endDate?: string,
  signalTypes?: string[]
): Promise<SimulatorSignal[]> {
  const params: Record<string, string> = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (signalTypes?.length) params.signals = signalTypes.join(',');

  const response = await api.get('/api/simulator/signals', { params });
  // API returns {signals: [...], count: N}
  return response.data.signals || [];
}

export async function getPerformanceMetrics(
  startDate?: string,
  endDate?: string,
  signalTypes?: string[]
): Promise<PerformanceMetrics> {
  const params: Record<string, string> = {};
  if (startDate) params.start_date = startDate;
  if (endDate) params.end_date = endDate;
  if (signalTypes?.length) params.signal_types = signalTypes.join(',');

  const response = await api.get('/api/simulator/performance', { params });
  return response.data;
}

// Stock Pool
export async function getStockPool(): Promise<{
  stocks: Array<{
    ticker: string;
    name: string;
    sector: string;
    market_cap: number;
    latest_disconnect_score?: number;
    latest_signal?: string;
  }>;
  total: number;
}> {
  const response = await api.get('/api/stocks/pool');
  return response.data;
}

export async function getSectorBreakdown(): Promise<Record<string, number>> {
  const response = await api.get('/api/stocks/sectors');
  return response.data;
}

// Auth
export async function login(email: string, password: string): Promise<{
  access_token: string;
  token_type: string;
  user: User;
}> {
  // Use /login/json endpoint for JSON-based authentication
  const response = await api.post('/api/auth/login/json', { email, password });
  return response.data;
}

export async function register(
  email: string,
  password: string,
  name?: string
): Promise<{
  access_token: string;
  token_type: string;
  user: User;
}> {
  const response = await api.post('/api/auth/register', { email, password, name });
  return response.data;
}

export async function getCurrentUser(token?: string): Promise<User> {
  if (token) {
    // Use raw axios to bypass interceptor when token is explicitly provided
    const response = await axios.get(`${API_BASE}/api/auth/me`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });
    return response.data;
  }
  const response = await api.get('/api/auth/me');
  return response.data;
}

export async function getAuthProviders(): Promise<{
  email_password: boolean;
  google: boolean;
  twitter: boolean;
}> {
  const response = await api.get('/api/auth/providers');
  return response.data;
}

// Health check
export async function healthCheck(): Promise<{ status: string }> {
  const response = await api.get('/api/health');
  return response.data;
}

// Batch update status (for showing when data was last refreshed)
export async function getBatchStatus(): Promise<{
  last_batch: {
    last_updated: string;
    collection_id?: string;
  } | null;
  cache_stats: {
    ttl_hours: number;
    fresh_entries: number;
    total_entries: number;
    fresh_tickers: number;
  };
  data_freshness: 'fresh' | 'stale' | 'no_data';
}> {
  const response = await api.get('/api/batch/status');
  return response.data;
}

// Historical data
export interface HistoryDataPoint {
  date: string;
  sentiment_score?: number;
  sentiment_label?: string;
  bullish_pct?: number;
  bearish_pct?: number;
  posts_analyzed?: number;
  disconnect_score?: number;
  signal?: string;
  fundamental_score?: number;
  price?: number;
  price_open?: number;
  price_high?: number;
  price_low?: number;
  volume?: number;
}

export interface StockHistoryResponse {
  ticker: string;
  history: HistoryDataPoint[];
  days: number;
  max_days_allowed: number;
  tier: string;
}

export async function getStockHistory(
  ticker: string,
  days: number = 30
): Promise<StockHistoryResponse> {
  const response = await api.get(`/api/history/${ticker}`, {
    params: { days },
  });
  return response.data;
}

// Custom ticker requests (PRO+)
export async function requestCustomTicker(ticker: string): Promise<{
  ticker: string;
  status: 'available' | 'queued' | 'pending';
  message: string;
  cached_at?: string;
}> {
  const response = await api.post(`/api/request/${ticker}`);
  return response.data;
}

export async function getTickerRequestStatus(ticker: string): Promise<{
  ticker: string;
  status: 'available' | 'pending';
  cached_at?: string;
  sentiment_score?: number;
}> {
  const response = await api.get(`/api/request/${ticker}/status`);
  return response.data;
}

// Tier configuration
export async function getTierConfig(): Promise<{
  tier: string;
  config: {
    max_stocks: number | null;
    custom_tickers: boolean;
    history_days: number;
    backtest_enabled: boolean;
    alerts_enabled: boolean;
    export_enabled: boolean;
    api_access: boolean;
  };
  all_tiers: string[];
}> {
  const response = await api.get('/api/tier/config');
  return response.data;
}

// Subscription management
export async function syncSubscription(): Promise<{
  synced: boolean;
  tier: string;
  status: string;
  expires_at?: string;
  message?: string;
}> {
  const response = await api.post('/api/stripe/sync-subscription');
  return response.data;
}

export async function getSubscriptionStatus(): Promise<{
  tier: string;
  status: string;
  stripe_customer_id?: string;
  subscription_expires_at?: string;
  limits: {
    max_stocks: number;
    max_scan_stocks: number;
    history_days: number;
    sentiment_history_days: number;
    update_interval_minutes: number;
    backtesting_years: number;
  };
  features: string[];
  permissions: {
    backtesting: boolean;
    api_access: boolean;
    export_data: boolean;
    custom_alerts: boolean;
    pattern_detection: boolean;
    real_time_updates: boolean;
    recovery_predictions: boolean;
    sector_analysis: boolean;
    correlation_analysis: boolean;
    custom_scoring: boolean;
    priority_support: boolean;
  };
}> {
  const response = await api.get('/api/stripe/subscription-status');
  return response.data;
}

// User limits
export interface UserLimits {
  tier: string;
  limits: {
    watchlist_enabled: boolean;
    max_watchlists: number | null;
    max_tickers_per_watchlist: number | null;
    max_stocks_analyzed: number;
    history_days: number;
    max_alerts: number | null;
    export_enabled: boolean;
    max_analyses_per_month: number | null;
    max_analyses_per_day: number | null;
    max_chat_messages_per_day: number | null;
  };
  usage: {
    watchlists?: number;
    total_tickers?: number;
    analyses_this_month?: number;
    analyses_today?: number;
    chat_messages_today?: number;
  };
  can_upgrade_to: string | null;
}

export async function getUserLimits(): Promise<UserLimits> {
  const response = await api.get('/api/user/limits');
  return response.data;
}

// User watchlists
export interface UserWatchlist {
  id: number;
  name: string;
  tickers: string[];
  is_default: boolean;
}

export async function getUserWatchlists(): Promise<{ watchlists: UserWatchlist[] }> {
  const response = await api.get('/api/user/watchlists');
  return response.data;
}

export async function createWatchlist(name: string, tickers: string[] = []): Promise<UserWatchlist> {
  const response = await api.post('/api/user/watchlists', { name, tickers });
  return response.data;
}

export async function updateWatchlist(
  watchlistId: number,
  data: { name?: string; tickers?: string[] }
): Promise<UserWatchlist> {
  const response = await api.put(`/api/user/watchlist/${watchlistId}`, data);
  return response.data;
}

export async function deleteWatchlist(watchlistId: number): Promise<{ deleted: boolean; id: number }> {
  const response = await api.delete(`/api/user/watchlist/${watchlistId}`);
  return response.data;
}

// User alerts
export interface UserAlert {
  id: number;
  name: string;
  ticker: string | null;
  min_disconnect_score: number | null;
  max_disconnect_score: number | null;
  min_sentiment: number | null;
  max_sentiment: number | null;
  signal_types: string[] | null;
  is_active: boolean;
  notify_email: boolean;
  last_triggered_at: string | null;
  trigger_count: number;
  created_at: string | null;
}

export async function getUserAlerts(): Promise<{ alerts: UserAlert[]; count: number; max_alerts: number | null }> {
  const response = await api.get('/api/user/alerts');
  return response.data;
}

export async function createAlert(data: {
  name: string;
  ticker?: string;
  min_disconnect_score?: number;
  signal_types?: string[];
  notify_email?: boolean;
}): Promise<{ id: number; name: string; created: boolean }> {
  const response = await api.post('/api/user/alerts', data);
  return response.data;
}

export async function updateAlert(
  alertId: number,
  data: {
    name?: string;
    ticker?: string;
    min_disconnect_score?: number;
    is_active?: boolean;
    notify_email?: boolean;
  }
): Promise<{ id: number; updated: boolean }> {
  const response = await api.put(`/api/user/alert/${alertId}`, data);
  return response.data;
}

export async function deleteAlert(alertId: number): Promise<{ deleted: boolean; id: number }> {
  const response = await api.delete(`/api/user/alert/${alertId}`);
  return response.data;
}

// User analysis history (view tracking with cached data)
export interface UserAnalysis {
  id: number;
  ticker: string;
  stock_name: string | null;
  sentiment_score: number | null;
  fundamental_score: number | null;
  technical_score: number | null;
  disconnect_score: number | null;
  signal: string | null;
  price_at_view: number | null;
  current_price: number | null;
  triggered_generation: boolean;
  analysis_data: Record<string, unknown> | null;
  viewed_at: string | null;
  cache_updated_at: string | null;
}

export interface TickerAnalysisDetail {
  ticker: string;
  stock_name: string | null;
  sentiment_score: number | null;
  fundamental_score: number | null;
  technical_score: number | null;
  disconnect_score: number | null;
  signal: string | null;
  current_price: number | null;
  analysis_data: Record<string, unknown> | null;
  cache_updated_at: string | null;
  user_first_viewed: string | null;
  user_triggered_generation: boolean;
}

export interface AnalysisCacheStatus {
  ticker: string;
  exists: boolean;
  is_fresh: boolean;
  needs_generation: boolean;
  cached_at: string | null;
  age_days: number | null;
  stock_name?: string;
  signal?: string;
  disconnect_score?: number;
}

export async function getUserAnalysisHistory(
  limit = 20,
  offset = 0
): Promise<{ analyses: UserAnalysis[]; total: number; limit: number; offset: number }> {
  const response = await api.get('/api/user/analysis-history', { params: { limit, offset } });
  return response.data;
}

export async function getUserAnalysisForTicker(ticker: string): Promise<TickerAnalysisDetail> {
  const response = await api.get(`/api/user/analysis-history/${ticker}`);
  return response.data;
}

export async function getAnalysisCacheStatus(ticker: string): Promise<AnalysisCacheStatus> {
  const response = await api.get(`/api/analysis/cache/${ticker}`);
  return response.data;
}

// Notifications
export interface UserNotification {
  id: number;
  title: string;
  message: string | null;
  ticker: string | null;
  type: 'alert' | 'system' | 'promo';
  disconnect_score: number | null;
  sentiment_score: number | null;
  signal: string | null;
  is_read: boolean;
  created_at: string | null;
}

export async function getUserNotifications(
  unreadOnly = false,
  limit = 20
): Promise<{ notifications: UserNotification[]; unread_count: number }> {
  const response = await api.get('/api/user/notifications', {
    params: { unread_only: unreadOnly, limit },
  });
  return response.data;
}

export async function markNotificationRead(notificationId: number): Promise<{ success: boolean; id: number }> {
  const response = await api.post(`/api/user/notifications/${notificationId}/read`);
  return response.data;
}

export async function markAllNotificationsRead(): Promise<{ success: boolean }> {
  const response = await api.post('/api/user/notifications/read-all');
  return response.data;
}

export async function deleteNotification(notificationId: number): Promise<{ deleted: boolean; id: number }> {
  const response = await api.delete(`/api/user/notifications/${notificationId}`);
  return response.data;
}

// Chat Conversations
export interface ChatConversationResponse {
  id: number;
  title: string;
  created_at: string;
  updated_at?: string;
  message_count?: number;
  messages?: {
    id: number;
    role: string;
    content: string;
    created_at: string;
  }[];
}

export async function getChatConversations(): Promise<{ conversations: ChatConversationResponse[] }> {
  const response = await api.get('/api/chat/conversations');
  return response.data;
}

export async function createChatConversation(title?: string): Promise<ChatConversationResponse> {
  const response = await api.post('/api/chat/conversations', { title });
  return response.data;
}

export async function getChatConversation(conversationId: number): Promise<ChatConversationResponse> {
  const response = await api.get(`/api/chat/conversations/${conversationId}`);
  return response.data;
}

export async function updateChatConversation(conversationId: number, title: string): Promise<{ id: number; title: string }> {
  const response = await api.patch(`/api/chat/conversations/${conversationId}`, { title });
  return response.data;
}

export async function deleteChatConversation(conversationId: number): Promise<{ success: boolean }> {
  const response = await api.delete(`/api/chat/conversations/${conversationId}`);
  return response.data;
}

export async function addChatMessage(conversationId: number, role: string, content: string): Promise<{
  id: number;
  role: string;
  content: string;
  created_at: string;
}> {
  const response = await api.post(`/api/chat/conversations/${conversationId}/messages`, { role, content });
  return response.data;
}

// ============================================================
// FUNDAMENTAL DATA API
// ============================================================

export interface FundamentalOverview {
  ticker: string;
  name?: string;
  sector?: string;
  industry?: string;
  description?: string;
  market_cap?: number;
  pe_ratio?: number;
  forward_pe?: number;
  peg_ratio?: number;
  price_to_book?: number;
  price_to_sales?: number;
  ev_to_ebitda?: number;
  ev_to_revenue?: number;
  profit_margin?: number;
  operating_margin?: number;
  gross_margin?: number;
  roe?: number;
  roa?: number;
  revenue_growth?: number;
  earnings_growth?: number;
  eps?: number;
  eps_diluted?: number;
  revenue_ttm?: number;
  gross_profit?: number;
  ebitda?: number;
  dividend_yield?: number;
  dividend_per_share?: number;
  payout_ratio?: number;
  book_value?: number;
  beta?: number;
  week_52_high?: number;
  week_52_low?: number;
  moving_avg_50?: number;
  moving_avg_200?: number;
  analyst_target_price?: number;
  current_price?: number;
}

export interface EarningsReport {
  fiscal_date: string;
  reported_date?: string;
  reported_eps?: number;
  estimated_eps?: number;
  surprise?: number;
  surprise_percentage?: number;
}

export interface EarningsData {
  ticker: string;
  annual_earnings: { fiscal_date: string; reported_eps?: number }[];
  quarterly_earnings: EarningsReport[];
}

export interface IncomeStatementReport {
  fiscal_date: string;
  reported_currency: string;
  total_revenue?: number;
  cost_of_revenue?: number;
  gross_profit?: number;
  operating_expenses?: number;
  operating_income?: number;
  income_before_tax?: number;
  income_tax_expense?: number;
  net_income?: number;
  ebitda?: number;
  research_and_development?: number;
  selling_general_administrative?: number;
}

export interface IncomeStatementData {
  ticker: string;
  annual_reports: IncomeStatementReport[];
  quarterly_reports: IncomeStatementReport[];
}

export interface CashFlowReport {
  fiscal_date: string;
  reported_currency: string;
  operating_cashflow?: number;
  depreciation_amortization?: number;
  capital_expenditures?: number;
  cashflow_from_investment?: number;
  cashflow_from_financing?: number;
  dividend_payout?: number;
  stock_repurchase?: number;
  stock_based_compensation?: number;
  net_shareholder_return?: number;
  debt_repayment?: number;
  change_in_cash?: number;
  free_cash_flow?: number;
}

export interface CashFlowData {
  ticker: string;
  annual_reports: CashFlowReport[];
  quarterly_reports: CashFlowReport[];
}

// Balance Sheet data for tracking shares outstanding and dilution
export interface BalanceSheetReport {
  fiscal_date: string;
  reported_currency: string;
  common_stock_shares_outstanding?: number;
  treasury_stock?: number;
  total_assets?: number;
  total_liabilities?: number;
  total_shareholder_equity?: number;
  retained_earnings?: number;
  common_stock?: number;
  cash_and_equivalents?: number;
  short_term_debt?: number;
  long_term_debt?: number;
}

export interface BalanceSheetData {
  ticker: string;
  annual_reports: BalanceSheetReport[];
  quarterly_reports: BalanceSheetReport[];
}

export interface WeeklyPrice {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface DailyPrice {
  date: string;
  open: number;
  high: number;
  low: number;
  close: number;
  volume: number;
}

export interface SectorValuationAverages {
  sector: string;
  pe_ratio_median: number | null;
  forward_pe_median: number | null;
  peg_ratio_median: number | null;
  price_to_book_median: number | null;
  price_to_sales_median: number | null;
  ev_to_ebitda_median: number | null;
  sample_size: number;
}

export interface FullFundamentalData {
  ticker: string;
  overview: FundamentalOverview;
  earnings: EarningsData;
  income_statement: IncomeStatementData;
  cash_flow: CashFlowData;
  balance_sheet: BalanceSheetData;
  daily_prices?: DailyPrice[];
  weekly_prices?: WeeklyPrice[];
  sector_averages?: SectorValuationAverages | null;
}

export async function getFundamentals(ticker: string): Promise<{ ticker: string; overview: FundamentalOverview }> {
  const response = await api.get(`/api/fundamentals/${ticker}`);
  return response.data;
}

export async function getEarningsHistory(ticker: string): Promise<EarningsData> {
  const response = await api.get(`/api/fundamentals/${ticker}/earnings`);
  return response.data;
}

export async function getIncomeStatement(ticker: string): Promise<IncomeStatementData> {
  const response = await api.get(`/api/fundamentals/${ticker}/income-statement`);
  return response.data;
}

export async function getCashFlow(ticker: string): Promise<CashFlowData> {
  const response = await api.get(`/api/fundamentals/${ticker}/cash-flow`);
  return response.data;
}

export async function getFullFundamentals(ticker: string): Promise<FullFundamentalData> {
  const response = await api.get(`/api/fundamentals/${ticker}/full`);
  return response.data;
}

// Fast price data endpoint - loads quickly for stock price chart
export interface StockPriceData {
  ticker: string;
  daily_prices: DailyPrice[];
  weekly_prices: WeeklyPrice[];
}

export async function getStockPrices(ticker: string): Promise<StockPriceData> {
  const response = await api.get(`/api/stock/${ticker}/prices`);
  return response.data;
}

// ============================================================
// EARNINGS TRANSCRIPTS API
// ============================================================

export async function getEarningsTranscripts(
  ticker: string,
  quarters: number = 12
): Promise<EarningsTranscriptsResponse> {
  const response = await api.get(`/api/earnings/${ticker}/transcripts`, {
    params: { quarters }
  });
  return response.data;
}

export async function ensureEarningsTranscripts(
  ticker: string
): Promise<{ ticker: string; status: string; count: number }> {
  const response = await api.post(`/api/earnings/${ticker}/ensure-transcripts`);
  return response.data;
}

// ============================================================
// RESEARCH API
// ============================================================

export async function getSentimentExtremes(params?: {
  ticker?: string;
  limit?: number;
  extreme_type?: 'lowest' | 'highest';
  days?: number;
}): Promise<SentimentExtremesResponse> {
  const response = await api.get('/api/research/sentiment-extremes', { params });
  return response.data;
}

export async function getTurnaroundAnalysis(params?: {
  min_sentiment?: number;
  days?: number;
}): Promise<TurnaroundAnalysis> {
  const response = await api.get('/api/research/turnaround-analysis', { params });
  return response.data;
}

export async function getOpportunityTrapAnalysis(params?: {
  days?: number;
}): Promise<OpportunityTrapAnalysis> {
  const response = await api.get('/api/research/opportunity-vs-trap', { params });
  return response.data;
}

export async function getPatternPerformance(params?: {
  pattern_type?: 'all' | 'sentiment_reversal' | 'high_disconnect' | 'fundamental_divergence';
  days?: number;
}): Promise<PatternPerformanceResponse> {
  const response = await api.get('/api/research/pattern-performance', { params });
  return response.data;
}

export async function getMarketResearchOverview(): Promise<MarketResearchOverview> {
  const response = await api.get('/api/research/market-overview');
  return response.data;
}

export async function getMA200Analysis(): Promise<MA200Analysis> {
  const response = await api.get('/api/research/ma200-analysis');
  return response.data;
}

// ============================================================
// API KEY MANAGEMENT (Enterprise)
// ============================================================

export interface ApiKeyData {
  id: number;
  name: string;
  prefix: string;
  created_at: string;
  last_used: string | null;
  total_requests: number;
  is_active: boolean;
}

export interface CreateApiKeyResponse {
  id: number;
  name: string;
  prefix: string;
  key: string;  // Full key - only shown once
  created_at: string;
  message: string;
}

export async function getApiKeys(): Promise<{ keys: ApiKeyData[] }> {
  const response = await api.get('/api/keys');
  return response.data;
}

// ============================================================
// WATCHLIST DATA
// ============================================================

export interface WatchlistStockData {
  ticker: string;
  name: string;
  sentiment_score: number;
  fundamental_score: number;
  disconnect_score: number;
  technical_score: number | null;
  signal: string;
  current_price: number | null;
  updated_at: string | null;
}

export async function getWatchlistStockData(tickers: string[]): Promise<{ stocks: Record<string, WatchlistStockData> }> {
  const response = await api.post('/api/watchlist/data', tickers);
  return response.data;  // Backend already returns { stocks: {...}, ... }
}

export async function createApiKey(
  name: string,
  permissions?: string[],
  rateLimit?: number
): Promise<CreateApiKeyResponse> {
  const response = await api.post('/api/keys', {
    name,
    permissions: permissions || ['read', 'analyze'],
    rate_limit: rateLimit || 60
  });
  return response.data;
}

export async function revokeApiKey(keyId: number): Promise<{ message: string; id: number }> {
  const response = await api.delete(`/api/keys/${keyId}`);
  return response.data;
}

export default api;
